import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
  computed,
  effect,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { PrivateNavbarFacade } from '@/app/layouts/private-layout/navbar/private-navbar.facade';
import { PRIVATE_NAVBAR_ICONS } from '@/app/layouts/private-layout/navbar/private-navbar.icons';
import { PrivateNavbarMenuComponent, PrivateNavbarMenuAction } from '@/app/layouts/private-layout/menu/menu';
import { CartStorageService } from '@/app/features/shared/cart/services/cart-storage.service';

@Component({
  selector: 'app-private-navbar',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    RouterLinkActive,
    TranslateModule,
    PrivateNavbarMenuComponent,
  ],
  templateUrl: './navbar.html',
  styleUrl: './navbar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrivateNavbarComponent implements AfterViewInit, OnDestroy {
  private readonly cartStorageService = inject(CartStorageService);

  @ViewChild('navbarRoot', { static: true }) navbarRoot!: ElementRef<HTMLElement>;

  searchValue = '';

  readonly icons = PRIVATE_NAVBAR_ICONS;
  readonly cartItemCount = this.cartStorageService.cartItemCount;
  readonly cartBadgeText = computed(() => {
    const count = this.cartItemCount();

    return count > 99 ? '99+' : `${count}`;
  });

  readonly menuOpen$: Observable<boolean>;

  navbarHeightPx = 0;
  private ro?: ResizeObserver;

  constructor(
    public facade: PrivateNavbarFacade,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {
    this.menuOpen$ = this.facade.menuOpen$;
    effect(() => {
      this.facade.searchClearedAt();
      this.searchValue = '';
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    const el = this.navbarRoot.nativeElement;

    const update = () => {
      this.navbarHeightPx = el.offsetHeight || 0;
      this.cdr.markForCheck();
    };

    update();

    this.ro = new ResizeObserver(() => update());
    this.ro.observe(el);
  }

  ngOnDestroy(): void {
    this.ro?.disconnect();
  }

  onSearchChange(value: string): void {
    this.searchValue = value ?? '';
  }

  async pasteLinkFromClipboard(): Promise<void> {
    const pastedValue = await this.readClipboardText();

    if (!pastedValue) {
      return;
    }

    this.ngZone.run(() => {
      this.onSearchChange(pastedValue);
      this.cdr.detectChanges();
    });
  }

  onMenuAction(action: PrivateNavbarMenuAction): void {
    this.facade.handleMenuAction(action);
  }

  private async readClipboardText(): Promise<string> {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.readText) {
      return '';
    }

    try {
      return (await navigator.clipboard.readText()).trim();
    } catch {
      return '';
    }
  }
}
